import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, TextField, FormControl, InputLabel, 
  Select, MenuItem, Switch, FormControlLabel, Paper, Chip, IconButton, 
  CircularProgress, Stack, useMediaQuery, InputAdornment, Divider, Alert,
  Tooltip, Fade, Drawer, Tab, Tabs, Popover, List, ListItem, ListItemText,
  Card, CardContent, CardActions, Badge, Collapse
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon, ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon,
  Description as DescriptionIcon, Security as SecurityIcon, CheckCircle as CheckCircleIcon,
  PersonAdd as PersonAddIcon, DragIndicator as DragIcon, Refresh as RefreshIcon,
  FormatBold as BoldIcon, FormatItalic as ItalicIcon, FormatUnderlined as UnderlineIcon,
  Undo as UndoIcon, Redo as RedoIcon, Menu as MenuIcon, Visibility as VisibilityIcon,
  Code as CodeIcon, ViewColumn as ViewColumnIcon, UploadFile as UploadFileIcon,
  PictureAsPdf as PdfIcon, Edit as EditIcon, Delete as DeleteIcon, Lightbulb as LightbulbIcon,
  Title as TitleIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import * as pdfjsLib from 'pdfjs-dist';
import client from "../../../../API";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ═══════════════════════════════════════════════════════════════════
// CUSTOM QUILL BLOT FOR VARIABLE CHIPS
// ═══════════════════════════════════════════════════════════════════
const Inline = Quill.import('blots/inline');

class VariableBlot extends Inline {
  static blotName = 'variable';
  static tagName = 'span';
  static className = 'ql-variable';

  static create(value) {
    const node = super.create(value);
    node.setAttribute('data-variable', value);
    node.contentEditable = 'false';
    node.style.backgroundColor = '#FEF3C7';
    node.style.border = '1px solid #fbbf24';
    node.style.borderRadius = '4px';
    node.style.padding = '2px 8px';
    node.style.margin = '0 2px';
    node.style.fontWeight = '500';
    node.style.cursor = 'default';
    node.style.display = 'inline-flex';
    node.style.alignItems = 'center';
    node.style.fontSize = '14px';
    node.innerText = `{{${value}}}`;
    return node;
  }

  static formats(node) {
    return node.getAttribute('data-variable');
  }

  static value(node) {
    return node.getAttribute('data-variable');
  }
}

Quill.register(VariableBlot);

// ═══════════════════════════════════════════════════════════════════
// VARIABLE CONTEXT MATCHER
// ═══════════════════════════════════════════════════════════════════
const VARIABLE_CONTEXT_MAP = [
  { keywords: ['company', 'corporation', 'business', 'entity', 'organization'], variable: 'COMPANY_NAME', confidence: 0.9 },
  { keywords: ['legal name', 'full legal name', 'official name', 'registered name'], variable: 'COMPANY_LEGAL_NAME', confidence: 0.95 },
  { keywords: ['employee', 'contractor', 'worker', 'individual', 'person', 'party'], variable: 'USER_NAME', confidence: 0.85 },
  { keywords: ['seller', 'vendor', 'provider', 'supplier'], variable: 'USER_NAME', confidence: 0.8 },
  { keywords: ['buyer', 'purchaser', 'client', 'customer'], variable: 'USER_NAME', confidence: 0.8 },
  { keywords: ['first name', 'given name'], variable: 'USER_FIRST_NAME', confidence: 0.95 },
  { keywords: ['last name', 'surname', 'family name'], variable: 'USER_LAST_NAME', confidence: 0.95 },
  { keywords: ['email', 'e-mail', 'electronic mail', 'email address'], variable: 'USER_EMAIL', confidence: 0.95 },
  { keywords: ['creator', 'influencer', 'content creator', 'artist'], variable: 'CREATOR_NAME', confidence: 0.9 },
  { keywords: ['effective date', 'start date', 'commencement date', 'beginning'], variable: 'SIGNATURE_DATE', confidence: 0.9 },
  { keywords: ['date', 'dated', 'this day', 'day of'], variable: 'CURRENT_DATE', confidence: 0.7 },
  { keywords: ['signature date', 'signed', 'execution date', 'executed'], variable: 'SIGNATURE_DATE', confidence: 0.95 },
  { keywords: ['title', 'name', 'agreement', 'contract'], variable: 'CONTRACT_TITLE', confidence: 0.6 },
];

const getVariableSuggestions = (textBefore, textAfter) => {
  const context = (textBefore.slice(-150) + ' ' + textAfter.slice(0, 150)).toLowerCase();
  
  const suggestions = VARIABLE_CONTEXT_MAP
    .map(item => {
      const matches = item.keywords.filter(keyword => context.includes(keyword));
      const score = matches.length > 0 ? item.confidence * (matches.length / item.keywords.length) : 0;
      return { variable: item.variable, score };
    })
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  return suggestions.length > 0 ? suggestions.map(s => s.variable) : ['USER_NAME', 'COMPANY_NAME', 'SIGNATURE_DATE'];
};

// ═══════════════════════════════════════════════════════════════════
// PDF UPLOAD COMPONENT
// ═══════════════════════════════════════════════════════════════════
const PdfUploader = memo(({ onPdfExtracted, onClose }) => {
  const [uploading, setUploading] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const extractTextFromPdf = async (file) => {
    try {
      setUploading(true);
      setError('');
      setPageCount(0);

      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ 
        data: arrayBuffer,
        verbosity: 0
      });

      const pdf = await loadingTask.promise;
      setPageCount(pdf.numPages);

      if (pdf.numPages === 0) {
        throw new Error('PDF has no pages');
      }

      let fullText = '';
      let hasText = false;

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        
        if (textContent.items.length > 0) {
          hasText = true;
          const pageText = textContent.items
            .map(item => item.str)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();
          
          if (pageText) {
            fullText += pageText + '\n\n';
          }
        }
      }

      if (!hasText || fullText.trim().length === 0) {
        throw new Error('PDF contains no extractable text. It may be a scanned image or have text in images.');
      }

      const cleanedText = fullText.trim();
      setExtractedText(cleanedText);
      return cleanedText;

    } catch (err) {
      console.error('PDF extraction error:', err);
      
      let errorMessage = 'Failed to extract text from PDF.';
      
      if (err.message.includes('no extractable text')) {
        errorMessage = 'This PDF contains no extractable text. It may be a scanned document or image-based PDF.';
      } else if (err.message.includes('Invalid PDF')) {
        errorMessage = 'Invalid PDF file. Please ensure the file is not corrupted.';
      } else if (err.message.includes('password')) {
        errorMessage = 'This PDF is password-protected. Please use an unprotected PDF.';
      } else {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
      setPageCount(0);
      return '';
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }

    setError('');
    setExtractedText('');
    setPdfFile(file);
    await extractTextFromPdf(file);
  };

  const handleUseExtractedText = () => {
    if (extractedText) {
      onPdfExtracted(extractedText);
    }
  };

  const handleClear = () => {
    setPdfFile(null);
    setExtractedText('');
    setPageCount(0);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 600 }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {!pdfFile ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            bgcolor: alpha('#4F46E5', 0.02),
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: alpha('#4F46E5', 0.05),
            }
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <PdfIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Upload PDF Contract
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload an existing PDF to extract its content and create a template
          </Typography>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            sx={{
              bgcolor: '#4F46E5',
              '&:hover': { bgcolor: '#4338CA' },
              textTransform: 'none'
            }}
          >
            Choose PDF File
          </Button>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
            Note: PDF must contain selectable text (not scanned images)
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <PdfIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {pdfFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(pdfFile.size / 1024).toFixed(1)} KB
                    {pageCount > 0 && ` • ${pageCount} page${pageCount !== 1 ? 's' : ''}`}
                  </Typography>
                </Box>
                <IconButton onClick={handleClear} color="error">
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </CardContent>

            {uploading && (
              <CardContent sx={{ pt: 0 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Extracting text from PDF...
                  </Typography>
                </Stack>
              </CardContent>
            )}

            {error && (
              <CardContent sx={{ pt: 0 }}>
                <Alert severity="error">{error}</Alert>
              </CardContent>
            )}

            {extractedText && !uploading && (
              <CardContent sx={{ pt: 0 }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Successfully extracted {extractedText.split(/\s+/).length} words from {pageCount} page{pageCount !== 1 ? 's' : ''}
                </Alert>
                
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Preview:
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    maxHeight: 200,
                    overflow: 'auto',
                    bgcolor: '#f9fafb',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {extractedText.substring(0, 500)}
                  {extractedText.length > 500 && '...'}
                </Paper>
              </CardContent>
            )}

            {extractedText && !uploading && (
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={handleClear}
                  sx={{ textTransform: 'none' }}
                >
                  Upload Different PDF
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleUseExtractedText}
                  sx={{
                    bgcolor: '#4F46E5',
                    '&:hover': { bgcolor: '#4338CA' },
                    textTransform: 'none'
                  }}
                >
                  Use This Content
                </Button>
              </CardActions>
            )}
          </Card>

          <Button
            variant="text"
            onClick={onClose}
            sx={{ textTransform: 'none' }}
          >
            Cancel and start from scratch
          </Button>
        </Stack>
      )}
    </Box>
  );
});

// ═══════════════════════════════════════════════════════════════════
// UNDERSCORE SUGGESTIONS PANEL
// ═══════════════════════════════════════════════════════════════════
const UnderscoreSuggestionsPanel = memo(({ underscores, onReplace, variables, onDismiss }) => {
  const [expanded, setExpanded] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (underscores.length === 0) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: isMobile ? 102 : 16, // Account for mobile footer
        right: 16,
        width: isMobile ? 'calc(100% - 32px)' : 380,
        maxWidth: isMobile ? 'calc(100% - 32px)' : 380,
        maxHeight: isMobile ? 300 : 400,
        overflow: 'hidden',
        zIndex: 1000,
        border: '2px solid #4F46E5',
      }}
    >
      <Box
        sx={{
          p: 2,
          bgcolor: '#4F46E5',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <LightbulbIcon />
          <Typography variant="subtitle2" fontWeight={600}>
            {underscores.length} Blank{underscores.length !== 1 ? 's' : ''} Found
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Badge badgeContent={underscores.length} color="error">
            <IconButton size="small" sx={{ color: 'white' }}>
              {expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Badge>
        </Stack>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ maxHeight: isMobile ? 220 : 320, overflow: 'auto' }}>
          {underscores.map((item, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                '&:last-child': { borderBottom: 'none' }
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Context: "{item.context}"
              </Typography>
              
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                {item.suggestions.map((varId) => {
                  const variable = variables.find(v => v.id === varId);
                  if (!variable) return null;

                  return (
                    <Chip
                      key={varId}
                      label={variable.label}
                      size="small"
                      onClick={() => onReplace(item, varId)}
                      sx={{
                        bgcolor: '#FEF3C7',
                        border: '1px solid #fbbf24',
                        fontWeight: 500,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: '#fde68a',
                        }
                      }}
                    />
                  );
                })}
              </Stack>

              <Button
                size="small"
                onClick={() => onDismiss(item)}
                sx={{ mt: 1, textTransform: 'none', fontSize: '11px' }}
              >
                Dismiss
              </Button>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
});

// ═══════════════════════════════════════════════════════════════════
// VARIABLE PALETTE COMPONENT (with drag support)
// ═══════════════════════════════════════════════════════════════════
const VariablePalette = memo(({ onInsertVariable, variables, isMobile = false }) => {
  const [search, setSearch] = useState('');
  const [draggedVariable, setDraggedVariable] = useState(null);

  const categoryLabels = {
    contract: 'Contract',
    company: 'Company',
    team: 'Team',
    user: 'User',
    creator: 'Creator',
    system: 'System',
    assignment: 'Assignment'
  };

  const filteredVariables = useMemo(() => {
    if (!search) return variables;
    return variables.filter(v => 
      v.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, variables]);

  const grouped = useMemo(() => {
    return filteredVariables.reduce((acc, v) => {
      if (!acc[v.category]) acc[v.category] = [];
      acc[v.category].push(v);
      return acc;
    }, {});
  }, [filteredVariables]);

  const handleDragStart = (e, variable) => {
    e.dataTransfer.setData('text/plain', variable.id);
    e.dataTransfer.effectAllowed = 'copy';
    setDraggedVariable(variable.id);
  };

  const handleDragEnd = () => {
    setDraggedVariable(null);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fafbfc' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: 'white' }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Insert Variables
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search variables..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          {isMobile ? 'Tap to insert' : 'Drag to editor or click to insert'}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {Object.entries(grouped).map(([category, vars]) => (
          <Box key={category} sx={{ mb: 3 }}>
            <Typography 
              variant="caption" 
              fontWeight={600} 
              color="text.secondary" 
              sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              {categoryLabels[category]}
            </Typography>
            <Stack spacing={0.75}>
              {vars.map(variable => (
                <Paper
                  key={variable.id}
                  draggable={!isMobile}
                  onDragStart={(e) => handleDragStart(e, variable)}
                  onDragEnd={handleDragEnd}
                  onClick={() => onInsertVariable(variable.id)}
                  sx={{
                    p: 1.5,
                    cursor: isMobile ? 'pointer' : 'grab',
                    border: '1px solid',
                    borderColor: draggedVariable === variable.id ? 'primary.main' : '#e0e0e0',
                    opacity: draggedVariable === variable.id ? 0.5 : 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: alpha('#4F46E5', 0.02),
                      transform: 'translateX(4px)',
                    },
                    '&:active': {
                      cursor: isMobile ? 'pointer' : 'grabbing',
                      bgcolor: alpha('#4F46E5', 0.08),
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {!isMobile && <DragIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                    <CodeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" fontWeight={500}>
                      {variable.label}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', pl: isMobile ? 0 : 4 }}>
                    {`{{${variable.id}}}`}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        ))}
      </Box>
    </Box>
  );
});

// ═══════════════════════════════════════════════════════════════════
// PAGINATED LIVE PREVIEW COMPONENT
// ═══════════════════════════════════════════════════════════════════
const LivePreview = memo(({ htmlContent, contractName, contractType, signatureSpots }) => {
  const previewRef = useRef(null);

  const generateFullContent = useCallback(() => {
    let content = htmlContent || '<p style="color: #9ca3af; font-style: italic;">Start typing to see your contract preview...</p>';
    
    const signatureSection = `
      <div style="margin: 48px 0; padding: 24px; background: #F9FAFB; border-radius: 8px; border: 1px solid #E5E7EB;">
        <div style="font-size: 13px; color: #6B7280; margin-bottom: 4px;">Company</div>
        <div style="font-size: 15px; font-weight: 600; margin-bottom: 16px;"><span style="background: #FEF3C7; padding: 2px 6px; border-radius: 4px; font-weight: 500;">{{COMPANY_NAME}}</span></div>
        <div style="font-size: 13px; color: #6B7280; margin-bottom: 4px;">Signatory</div>
        <div style="font-size: 15px; font-weight: 600; margin-bottom: 16px;"><span style="background: #FEF3C7; padding: 2px 6px; border-radius: 4px; font-weight: 500;">{{USER_NAME}}</span></div>
        <div style="font-size: 13px; color: #6B7280; margin-bottom: 4px;">Email</div>
        <div style="font-size: 15px; font-weight: 600; margin-bottom: 16px;"><span style="background: #FEF3C7; padding: 2px 6px; border-radius: 4px; font-weight: 500;">{{USER_EMAIL}}</span></div>
        <div style="font-size: 13px; color: #6B7280; margin-bottom: 4px;">Date</div>
        <div style="font-size: 15px; font-weight: 600;"><span style="background: #FEF3C7; padding: 2px 6px; border-radius: 4px; font-weight: 500;">{{SIGNATURE_DATE}}</span></div>
      </div>
    `;

    let agreementSection = '';
    if (contractType === 'simple') {
      agreementSection = `
        <div style="margin-top: 48px; text-align: center; padding: 32px; background: #EEF2FF; border-radius: 12px;">
          <p style="margin-bottom: 20px; font-weight: 600; font-size: 16px; color: #4F46E5;">By clicking below, you agree to all terms above</p>
          <button style="background: #4F46E5; color: white; padding: 16px 48px; border: none; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);">
            ✓ I Agree
          </button>
        </div>
      `;
    } else if (signatureSpots.length > 0) {
      agreementSection = '<div style="margin-top: 64px;">';
      signatureSpots.forEach(spot => {
        agreementSection += `<div style="border: 2px dashed #4F46E5; padding: 32px 24px; text-align: center; margin: 24px 0; border-radius: 8px; background: #F9FAFB;">
          <div style="color: #6B7280; font-size: 12px; margin-bottom: 16px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px;">${spot.label}</div>
          <div style="height: 60px; border-bottom: 2px solid #1F2937; margin: 16px auto; max-width: 320px;"></div>
          <div style="font-size: 11px; color: #9CA3AF; margin-top: 12px;">Digital Signature</div>
        </div>`;
      });
      agreementSection += '</div>';
    }

    const fullContent = content + signatureSection + agreementSection;

    // Highlight variables
    const highlightedContent = fullContent.replace(/\{\{([^}]+)\}\}/g, (match) => {
      return `<span style="background: #FEF3C7; padding: 2px 6px; border-radius: 4px; font-weight: 500; border: 1px solid #fbbf24;">${match}</span>`;
    });

    return highlightedContent;
  }, [htmlContent, contractType, signatureSpots]);

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: '#f7f9fc'
    }}>
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid #e0e0e0',
        bgcolor: 'white'
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" fontWeight={600}>
            Live Preview
          </Typography>
          <Chip label="Updates automatically" size="small" variant="outlined" />
        </Stack>
      </Box>
      
      <Box 
        ref={previewRef}
        sx={{ 
          flex: 1, 
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          p: { xs: 2, md: 4 },
          bgcolor: '#e5e7eb'
        }}
      >
        <Paper 
          elevation={3}
          sx={{ 
            width: '8.5in',
            maxWidth: '100%',
            minHeight: '11in', // Changed from fixed height to minHeight
            bgcolor: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            mb: 4, // Add bottom margin for spacing
            '@media print': {
              boxShadow: 'none',
              margin: 0,
              width: '100%',
            }
          }}
        >
          {/* Title Header */}
          <Box sx={{ 
            p: '1in',
            pb: 3,
            borderBottom: '3px solid #4F46E5' 
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: '#1a1a1a',
                fontSize: '32px',
                textAlign: 'center'
              }}
            >
              {contractName || 'Untitled Contract'}
            </Typography>
          </Box>

          {/* Content with proper formatting */}
          <Box 
            sx={{
              p: '1in',
              pt: 6,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              lineHeight: 1.8,
              color: '#1a1a1a',
              fontSize: '15px',
              '& p': { marginBottom: '20px', lineHeight: 1.8 },
              '& h1': { fontSize: '24px', fontWeight: 600, marginTop: '32px', marginBottom: '16px' },
              '& h2': { fontSize: '20px', fontWeight: 600, marginTop: '32px', marginBottom: '16px' },
              '& ol, & ul': { marginTop: '16px', marginBottom: '16px', paddingLeft: '24px' },
              '& li': { marginBottom: '12px' },
              '& strong': { fontWeight: 600 },
              '& em': { fontStyle: 'italic' },
              '& u': { textDecoration: 'underline' },
            }}
            dangerouslySetInnerHTML={{ __html: generateFullContent() }}
          />
        </Paper>
      </Box>
    </Box>
  );
});

// ═══════════════════════════════════════════════════════════════════
// CUSTOM QUILL TOOLBAR
// ═══════════════════════════════════════════════════════════════════
const CustomToolbar = ({ canUndo, canRedo, onUndo, onRedo }) => (
  <Box id="toolbar" sx={{ 
    borderBottom: '1px solid #e0e0e0',
    bgcolor: 'white',
    p: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1
  }}>
    <Stack direction="row" spacing={0.5}>
      <Tooltip title="Undo (Ctrl+Z)">
        <span>
          <IconButton size="small" onClick={onUndo} disabled={!canUndo}>
            <UndoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Redo (Ctrl+Shift+Z)">
        <span>
          <IconButton size="small" onClick={onRedo} disabled={!canRedo}>
            <RedoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>

    <Divider orientation="vertical" flexItem />

    <button className="ql-bold" />
    <button className="ql-italic" />
    <button className="ql-underline" />
    
    <Divider orientation="vertical" flexItem />
    
    <button className="ql-list" value="ordered" />
    <button className="ql-list" value="bullet" />
    
    <Divider orientation="vertical" flexItem />
    
    <select className="ql-header">
      <option value="1">Heading 1</option>
      <option value="2">Heading 2</option>
      <option value="">Normal</option>
    </select>
  </Box>
);

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const EnhancedContractTemplateDialog = ({ 
  open, 
  template, 
  onClose, 
  onSave,
  userMode = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [step, setStep] = useState(0);
  const [showPdfUpload, setShowPdfUpload] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contract_type: 'simple',
    is_mandatory: false,
    expires_after_days: '',
    requires_resignation_on_update: false,
  });
  
  const [htmlContent, setHtmlContent] = useState('');
  const [signatureSpots, setSignatureSpots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [mobileView, setMobileView] = useState('editor');
  const [variableDrawerOpen, setVariableDrawerOpen] = useState(false);
  const [detectedUnderscores, setDetectedUnderscores] = useState([]);
  
  const quillRef = useRef(null);
  const editorContainerRef = useRef(null);
  const isUpdatingFromHistory = useRef(false);
  const checkUnderscoresTimeout = useRef(null);

  const variables = [
    { id: 'CONTRACT_TITLE', label: 'Contract Title', category: 'contract' },
    { id: 'COMPANY_NAME', label: 'Company Name', category: 'company' },
    { id: 'COMPANY_LEGAL_NAME', label: 'Company Legal Name', category: 'company' },
    { id: 'USER_NAME', label: 'User Full Name', category: 'user' },
    { id: 'USER_FIRST_NAME', label: 'User First Name', category: 'user' },
    { id: 'USER_LAST_NAME', label: 'User Last Name', category: 'user' },
    { id: 'USER_EMAIL', label: 'User Email', category: 'user' },
    { id: 'CREATOR_NAME', label: 'Creator Name', category: 'creator' },
    { id: 'SIGNATURE_DATE', label: 'Signature Date', category: 'system' },
    { id: 'CURRENT_DATE', label: 'Current Date', category: 'system' },
  ];

  const modules = useMemo(() => ({
    toolbar: {
      container: "#toolbar",
    },
    clipboard: {
      matchVisual: false,
    },
    keyboard: {
      bindings: {
        undo: {
          key: 'Z',
          ctrlKey: true,
          shiftKey: false,
          handler: () => {
            handleUndo();
            return false;
          }
        },
        redo: {
          key: 'Z',
          ctrlKey: true,
          shiftKey: true,
          handler: () => {
            handleRedo();
            return false;
          }
        },
      }
    }
  }), []);

  const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'header',
    'variable'
  ];

  // Add this useEffect right after your existing template useEffect
useEffect(() => {
  if (!open) {
    // Reset all state when dialog closes
    setStep(0);
    setShowPdfUpload(false);
    setFormData({
      name: '',
      description: '',
      contract_type: 'simple',
      is_mandatory: false,
      expires_after_days: '',
      requires_resignation_on_update: false,
    });
    const defaultContent = getDefaultContent();
    setHtmlContent(defaultContent);
    setHistory([defaultContent]);
    setHistoryIndex(0);
    setSignatureSpots([]);
    setMobileView('editor');
    setVariableDrawerOpen(false);
    setDetectedUnderscores([]);
  }
}, [open]);

// Also update the existing template useEffect to reset step
useEffect(() => {
  if (template) {
    setStep(0); // Add this line
    setFormData({
      name: template.name || '',
      description: template.description || '',
      contract_type: template.contract_type || 'simple',
      is_mandatory: template.is_mandatory ?? false,
      expires_after_days: template.expires_after_days || '',
      requires_resignation_on_update: template.requires_resignation_on_update ?? false,
    });
    
    const extracted = extractHtmlFromTemplate(template.contract_body || '');
    setHtmlContent(extracted);
    setHistory([extracted]);
    setHistoryIndex(0);
    extractSignatureSpots(template.contract_body || '');
  } else {
    setStep(0); // Add this line
    const defaultContent = getDefaultContent();
    setHtmlContent(defaultContent);
    setHistory([defaultContent]);
    setHistoryIndex(0);
  }
}, [template]);

  const getDefaultContent = () => {
    return `<h1>EMPLOYMENT AGREEMENT</h1>
<p><br></p>
<p>This Employment Agreement is entered into between the Company and the Employee.</p>
<p><br></p>
<h2>1. Position and Duties</h2>
<p>The employee agrees to perform duties as assigned by the company.</p>
<p><br></p>
<h2>2. Compensation</h2>
<p>The employee will receive compensation as agreed upon.</p>
<p><br></p>
<h2>3. Effective Date</h2>
<p>This agreement is effective as of the signature date.</p>
<p><br></p>
<p>By signing below, both parties agree to the terms outlined in this agreement.</p>`;
  };

  const extractHtmlFromTemplate = (html) => {
    if (!html) return '';
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Remove the outer wrapper div if present
    let contentDiv = tempDiv.querySelector('div[style*="font-family"]') || tempDiv;
    
    // Clone to avoid modifying original
    const workingDiv = contentDiv.cloneNode(true);
    
    // Remove title header section (div with border-bottom containing h1)
    const titleSection = workingDiv.querySelector('div[style*="border-bottom"]');
    if (titleSection) titleSection.remove();
    
    // Remove signature info section (div with background #F9FAFB)
    const signatureSections = workingDiv.querySelectorAll('div[style*="#F9FAFB"]');
    signatureSections.forEach(section => section.remove());
    
    // Remove agreement button section (div with background #EEF2FF)
    const agreementSection = workingDiv.querySelector('div[style*="#EEF2FF"]');
    if (agreementSection) agreementSection.remove();
    
    // Remove signature spot containers
    const signatureSpotContainers = workingDiv.querySelectorAll('div[style*="margin-top: 64px"]');
    signatureSpotContainers.forEach(container => {
      // Check if it contains signature spot markers
      if (container.innerHTML.includes('SIGNATURE_SPOT')) {
        container.remove();
      }
    });
    
    // Get the remaining content
    let extractedHtml = workingDiv.innerHTML.trim();
    
    // Clean up any remaining wrapper divs that are just containers
    const cleanDiv = document.createElement('div');
    cleanDiv.innerHTML = extractedHtml;
    
    // If there's just one wrapping div with no meaningful attributes, unwrap it
    if (cleanDiv.children.length === 1 && cleanDiv.children[0].tagName === 'DIV') {
      const onlyChild = cleanDiv.children[0];
      // Check if it's just a wrapper (no id, class, or important styles)
      if (!onlyChild.id && !onlyChild.className) {
        extractedHtml = onlyChild.innerHTML;
      }
    }
    
    return extractedHtml || html;
  };

  const extractSignatureSpots = (content) => {
    const spots = [];
    const regex = /\{\{SIGNATURE_SPOT:([^:]+):([^}]+)\}\}/g;
    let match;
    while ((match = regex.exec(content)) !== null) {
      spots.push({ id: match[2], label: match[1] });
    }
    setSignatureSpots(spots);
  };

  const handlePdfExtracted = useCallback((text) => {
    const paragraphs = text.split('\n\n').filter(p => p.trim());
    const htmlFromPdf = paragraphs
      .map(p => `<p>${p.trim()}</p>`)
      .join('');
    
    updateContentWithHistory(htmlFromPdf);
    setShowPdfUpload(false);
    setStep(1);
  }, []);

  const updateContentWithHistory = useCallback((newContent) => {
    if (isUpdatingFromHistory.current) return;
    
    setHtmlContent(newContent);
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newContent);
      if (newHistory.length > 50) {
        newHistory.shift();
        setHistoryIndex(prev => prev);
        return newHistory;
      }
      setHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
  }, [historyIndex]);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUpdatingFromHistory.current = true;
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setHtmlContent(history[newIndex]);
      
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        quill.clipboard.dangerouslyPasteHTML(history[newIndex]);
      }
      
      setTimeout(() => {
        isUpdatingFromHistory.current = false;
      }, 0);
    }
  }, [historyIndex, history]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUpdatingFromHistory.current = true;
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setHtmlContent(history[newIndex]);
      
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        quill.clipboard.dangerouslyPasteHTML(history[newIndex]);
      }
      
      setTimeout(() => {
        isUpdatingFromHistory.current = false;
      }, 0);
    }
  }, [historyIndex, history]);

  // Detect ALL underscores in document
  const checkForUnderscores = useCallback((content) => {
    if (!quillRef.current) return;

    const quill = quillRef.current.getEditor();
    const text = quill.getText();
    
    const underscorePattern = /_{3,}/g;
    const foundUnderscores = [];
    let match;
    
    while ((match = underscorePattern.exec(text)) !== null) {
      const startIndex = match.index;
      const endIndex = match.index + match[0].length;
      
      const textBefore = text.substring(Math.max(0, startIndex - 150), startIndex);
      const textAfter = text.substring(endIndex, Math.min(text.length, endIndex + 150));
      
      const contextSnippet = (textBefore.slice(-30) + match[0] + textAfter.slice(0, 30)).trim();
      
      const suggestions = getVariableSuggestions(textBefore, textAfter);
      
      foundUnderscores.push({
        start: startIndex,
        end: endIndex,
        text: match[0],
        context: contextSnippet,
        suggestions: suggestions
      });
    }
    
    setDetectedUnderscores(foundUnderscores);
  }, []);

  const handleContentChange = useCallback((content) => {
    updateContentWithHistory(content);
    
    if (checkUnderscoresTimeout.current) {
      clearTimeout(checkUnderscoresTimeout.current);
    }
    
    checkUnderscoresTimeout.current = setTimeout(() => {
      checkForUnderscores(content);
    }, 1000);
  }, [updateContentWithHistory, checkForUnderscores]);

  const insertVariable = useCallback((variableId) => {
    if (!quillRef.current) return;

    const quill = quillRef.current.getEditor();
    const range = quill.getSelection(true);
    
    quill.insertEmbed(range.index, 'variable', variableId);
    quill.insertText(range.index + 1, ' ');
    quill.setSelection(range.index + 2);
    
    if (isMobile) {
      setVariableDrawerOpen(false);
    }
  }, [isMobile]);

  const handleUnderscoreReplace = useCallback((underscoreItem, variableId) => {
    if (!quillRef.current) return;

    const quill = quillRef.current.getEditor();
    
    quill.deleteText(underscoreItem.start, underscoreItem.end - underscoreItem.start);
    quill.insertEmbed(underscoreItem.start, 'variable', variableId);
    quill.insertText(underscoreItem.start + 1, ' ');
    quill.setSelection(underscoreItem.start + 2);
    
    // Refresh underscore detection
    setTimeout(() => {
      checkForUnderscores(quill.root.innerHTML);
    }, 100);
  }, [checkForUnderscores]);

  const handleDismissUnderscore = useCallback((underscoreItem) => {
    setDetectedUnderscores(prev => prev.filter(u => u.start !== underscoreItem.start));
  }, []);

  // FIXED: Handle drop at actual drop location
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    
    if (!quillRef.current || !editorContainerRef.current) return;
    
    const variableId = e.dataTransfer.getData('text/plain');
    if (!variableId) return;

    const quill = quillRef.current.getEditor();
    
    // Get the Quill editor element
    const editorElement = quill.root;
    
    // Calculate relative position within the editor
    const editorRect = editorElement.getBoundingClientRect();
    const x = e.clientX - editorRect.left;
    const y = e.clientY - editorRect.top;
    
    // Get the drop position index from Quill
    const range = quill.getBounds(0);
    let dropIndex = 0;
    
    // Find the closest position by iterating through the document
    const length = quill.getLength();
    for (let i = 0; i < length; i++) {
      const bounds = quill.getBounds(i);
      if (bounds.top <= y && y <= bounds.top + bounds.height) {
        if (x >= bounds.left) {
          dropIndex = i;
        } else {
          break;
        }
      }
    }
    
    // Insert the variable at the drop position
    quill.insertEmbed(dropIndex, 'variable', variableId);
    quill.insertText(dropIndex + 1, ' ');
    quill.setSelection(dropIndex + 2);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const addSignatureSpot = useCallback(() => {
    const label = prompt('Signature label (e.g., "Employee Signature"):', 'Employee Signature');
    if (!label) return;
    
    const spotId = `spot_${Date.now()}`;
    
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      const spotHtml = `<p><br></p><p style="background-color: #fef3c7; padding: 8px; border-radius: 4px;">{{SIGNATURE_SPOT:${label}:${spotId}}}</p><p><br></p>`;
      quill.clipboard.dangerouslyPasteHTML(range.index, spotHtml);
    }
    
    setSignatureSpots(prev => [...prev, { label, id: spotId }]);
  }, []);

  const convertToFinalHtml = useCallback((content) => {
    let processedContent = content.replace(
      /<span[^>]*data-variable="([^"]*)"[^>]*>.*?<\/span>/g,
      '{{$1}}'
    );

    let html = `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 800px; margin: 0 auto; line-height: 1.8; color: #1a1a1a;">
  <div style="text-align: center; margin-bottom: 48px; padding-bottom: 24px; border-bottom: 3px solid #4F46E5;">
    <h1 style="font-size: 32px; font-weight: 700; color: #1a1a1a; margin: 0;">{{CONTRACT_TITLE}}</h1>
  </div>
  ${processedContent}
  <div style="margin: 48px 0; padding: 24px; background: #F9FAFB; border-radius: 8px; border: 1px solid #E5E7EB;">
    <div style="font-size: 13px; color: #6B7280; margin-bottom: 4px;">Company</div>
    <div style="font-size: 15px; font-weight: 600; margin-bottom: 16px;">{{COMPANY_NAME}}</div>
    <div style="font-size: 13px; color: #6B7280; margin-bottom: 4px;">Signatory</div>
    <div style="font-size: 15px; font-weight: 600; margin-bottom: 16px;">{{USER_NAME}}</div>
    <div style="font-size: 13px; color: #6B7280; margin-bottom: 4px;">Email</div>
    <div style="font-size: 15px; font-weight: 600; margin-bottom: 16px;">{{USER_EMAIL}}</div>
    <div style="font-size: 13px; color: #6B7280; margin-bottom: 4px;">Date</div>
    <div style="font-size: 15px; font-weight: 600;">{{SIGNATURE_DATE}}</div>
  </div>
`;

    if (formData.contract_type === 'simple') {
      html += `
  <div style="margin-top: 48px; text-align: center; padding: 32px; background: #EEF2FF; border-radius: 12px;">
    <p style="margin-bottom: 20px; font-weight: 600; font-size: 16px; color: #4F46E5;">By clicking below, you agree to all terms above</p>
    {{SIMPLE_AGREEMENT_BUTTON}}
  </div>
`;
    } else if (signatureSpots.length > 0) {
      html += `  <div style="margin-top: 64px;">`;
      signatureSpots.forEach(spot => {
        html += `    {{SIGNATURE_SPOT:${spot.label}:${spot.id}}}\n`;
      });
      html += '  </div>\n';
    }

    html += '</div>';
    return html;
  }, [formData.contract_type, signatureSpots]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('Template name is required');
      return;
    }

    if (!htmlContent.trim()) {
      alert('Document content is required');
      return;
    }

    const finalContent = convertToFinalHtml(htmlContent);

    try {
      setLoading(true);

      const dataToSend = {
        name: formData.name,
        description: formData.description,
        js_content: finalContent,
        contract_body: finalContent,
        contract_type: formData.contract_type,
        expires_after_days: formData.expires_after_days === '' ? null : parseInt(formData.expires_after_days, 10),
        requires_resignation_on_update: formData.requires_resignation_on_update,
        is_mandatory: formData.is_mandatory,
        metadata: {
          contract_type: formData.contract_type,
          signature_spots: signatureSpots,
        }
      };

      if (template) {
        await client.contracts.updateTemplate(template.id, dataToSend);
      } else {
        await client.contracts.createTemplate(dataToSend);
      }
      
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 0) return formData.name.trim().length > 0;
    if (step === 1) return htmlContent.trim().length > 0;
    return true;
  };

  if (!open) return null;

  const headerHeight = step === 0 ? 70 : 56;

  const renderMobileEditor = () => (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      pb: '86px' // Space for mobile footer
    }}>
      <Tabs 
        value={mobileView} 
        onChange={(e, v) => setMobileView(v)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}
      >
        <Tab label="Editor" value="editor" />
        <Tab label="Preview" value="preview" />
      </Tabs>

      {mobileView === 'editor' && (
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <CustomToolbar 
            canUndo={historyIndex > 0}
            canRedo={historyIndex < history.length - 1}
            onUndo={handleUndo}
            onRedo={handleRedo}
          />
          
          <Box 
            ref={editorContainerRef}
            sx={{ 
              flex: 1, 
              overflow: 'auto', 
              '& .ql-container': { 
                border: 'none', 
                fontSize: '15px',
                height: '100%'
              },
              '& .ql-editor': {
                minHeight: '100%',
                padding: '16px'
              }
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={htmlContent}
              onChange={handleContentChange}
              modules={modules}
              formats={formats}
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              placeholder="Start typing your contract here..."
            />
          </Box>

          <Button
            variant="contained"
            onClick={() => setVariableDrawerOpen(true)}
            sx={{
              position: 'fixed',
              bottom: 102, // Above mobile footer
              right: 16,
              borderRadius: '50%',
              minWidth: 56,
              height: 56,
              bgcolor: '#4F46E5',
              '&:hover': { bgcolor: '#4338CA' },
              zIndex: 1000,
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.4)'
            }}
          >
            <CodeIcon />
          </Button>
        </Box>
      )}

      {mobileView === 'preview' && (
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <LivePreview 
            htmlContent={htmlContent}
            contractName={formData.name}
            contractType={formData.contract_type}
            signatureSpots={signatureSpots}
          />
        </Box>
      )}

      <Drawer
        anchor="bottom"
        open={variableDrawerOpen}
        onClose={() => setVariableDrawerOpen(false)}
        PaperProps={{
          sx: { 
            height: '70vh', 
            borderTopLeftRadius: 16, 
            borderTopRightRadius: 16,
            maxHeight: 'calc(100vh - 100px)' // Ensure it doesn't cover everything
          }
        }}
      >
        <Box sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" fontWeight={600}>Insert Variable</Typography>
          <IconButton onClick={() => setVariableDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <VariablePalette 
            onInsertVariable={insertVariable}
            variables={variables}
            isMobile={true}
          />
        </Box>
      </Drawer>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'white' }}>
        <Box sx={{ 
          height: headerHeight,
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: step === 0 ? { xs: 2, md: 4 } : 2,
          bgcolor: 'white',
          transition: 'all 0.3s',
          zIndex: 10
        }}>
          {step === 0 ? (
            <>
              <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 3 }}>
                <Box sx={{ 
                  width: { xs: 36, md: 40 }, 
                  height: { xs: 36, md: 40 }, 
                  borderRadius: 1.5,
                  bgcolor: '#4F46E5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <DescriptionIcon sx={{ color: 'white', fontSize: { xs: 20, md: 22 } }} />
                </Box>
                <Box>
                  <Typography variant={{ xs: 'subtitle1', md: 'h6' }} fontWeight={600} color="text.primary">
                    {template ? 'Edit Template' : 'New Contract Template'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                    {showPdfUpload ? 'Upload a PDF to extract content' : 'Start by naming your contract'}
                  </Typography>
                </Box>
              </Stack>

              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </>
          ) : (
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton onClick={() => setStep(step - 1)} size="small">
                  <ArrowBackIcon />
                </IconButton>
                <TextField
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  variant="standard"
                  placeholder="Contract Title"
                  sx={{
                    minWidth: 200,
                    '& .MuiInput-root': {
                      fontSize: { xs: '14px', md: '16px' },
                      fontWeight: 600,
                      '&:before': { borderBottom: 'none' },
                      '&:hover:before': { borderBottom: '1px solid rgba(0,0,0,0.12)' },
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <TitleIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                    )
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center">
                {!isMobile && step === 1 && (
                  <Button
                    variant="outlined"
                    onClick={() => setStep(2)}
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    Settings
                  </Button>
                )}
                {!isMobile && (
                  <Chip 
                    label={step === 1 ? 'Editing Document' : 'Settings'} 
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={loading || !canProceed()}
                  startIcon={loading ? <CircularProgress size={18} /> : <CheckCircleIcon />}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ 
                    bgcolor: '#4F46E5',
                    '&:hover': { bgcolor: '#4338CA' },
                    textTransform: 'none',
                  }}
                >
                  {loading ? 'Saving...' : (template ? 'Update' : 'Create')}
                </Button>
                <IconButton onClick={onClose} size="small">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </>
          )}
        </Box>

        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {step === 0 ? (
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: { xs: 2, md: 4 },
              pb: isMobile ? '102px' : 4 // Space for mobile footer
            }}>
              {showPdfUpload ? (
                <PdfUploader 
                  onPdfExtracted={handlePdfExtracted}
                  onClose={() => setShowPdfUpload(false)}
                />
              ) : (
                <Box sx={{ maxWidth: 600, width: '100%' }}>
                  <Stack spacing={3}>
                    <TextField
                      label="Contract Name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      fullWidth
                      required
                      autoFocus
                      placeholder="e.g., Employment Agreement"
                      helperText="This will appear as the document title"
                    />
                    
                    <TextField
                      label="Description"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Brief description for internal reference (optional)"
                    />

                    <Divider>
                      <Chip label="Choose how to start" size="small" />
                    </Divider>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => setShowPdfUpload(true)}
                        startIcon={<UploadFileIcon />}
                        fullWidth
                        sx={{ 
                          textTransform: 'none',
                          py: 1.5,
                          borderColor: '#4F46E5',
                          color: '#4F46E5',
                          '&:hover': {
                            borderColor: '#4338CA',
                            bgcolor: alpha('#4F46E5', 0.04)
                          }
                        }}
                      >
                        Upload PDF
                      </Button>

                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => setStep(1)}
                        disabled={!canProceed()}
                        endIcon={<ArrowForwardIcon />}
                        fullWidth
                        sx={{ 
                          bgcolor: '#4F46E5',
                          '&:hover': { bgcolor: '#4338CA' },
                          textTransform: 'none',
                          py: 1.5
                        }}
                      >
                        Start from Scratch
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              )}
            </Box>
          ) : step === 1 ? (
            isMobile ? (
              renderMobileEditor()
            ) : (
              <>
                <Box sx={{ 
                  width: 280,
                  borderRight: '1px solid #e0e0e0',
                  overflow: 'hidden'
                }}>
                  <VariablePalette 
                    onInsertVariable={insertVariable}
                    variables={variables}
                    isMobile={false}
                  />
                </Box>

                <Box sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRight: '1px solid #e0e0e0',
                  position: 'relative'
                }}>
                  <Box sx={{ 
                    p: 2, 
                    borderBottom: '1px solid #e0e0e0',
                    bgcolor: 'white'
                  }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Document Editor
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Drag variables to drop location • Use ___ for smart suggestions
                    </Typography>
                  </Box>

                  <CustomToolbar 
                    canUndo={historyIndex > 0}
                    canRedo={historyIndex < history.length - 1}
                    onUndo={handleUndo}
                    onRedo={handleRedo}
                  />

                  <Box 
                    ref={editorContainerRef}
                    sx={{ 
                      flex: 1, 
                      overflow: 'auto',
                      '& .ql-container': { 
                        border: 'none',
                        fontFamily: 'Georgia, serif',
                        fontSize: '15px',
                        lineHeight: 1.8,
                      },
                      '& .ql-editor': {
                        padding: '24px',
                        minHeight: '100%'
                      },
                      '& .ql-variable': {
                        userSelect: 'none'
                      }
                    }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={htmlContent}
                      onChange={handleContentChange}
                      modules={modules}
                      formats={formats}
                      placeholder="Start typing your contract here... Drag variables from the left or use ___ for blanks."
                    />
                  </Box>

                  {/* Underscore Suggestions Panel */}
                  <UnderscoreSuggestionsPanel
                    underscores={detectedUnderscores}
                    onReplace={handleUnderscoreReplace}
                    variables={variables}
                    onDismiss={handleDismissUnderscore}
                  />
                </Box>

                <Box sx={{ width: '45%', overflow: 'hidden' }}>
                  <LivePreview 
                    htmlContent={htmlContent}
                    contractName={formData.name}
                    contractType={formData.contract_type}
                    signatureSpots={signatureSpots}
                  />
                </Box>
              </>
            )
          ) : (
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: { xs: 2, md: 4 },
              pb: isMobile ? '102px' : 4, // Space for mobile footer
              overflow: 'auto'
            }}>
              <Box sx={{ maxWidth: 600, width: '100%' }}>
                <Stack spacing={3}>
                  <Typography variant="h6" fontWeight={600}>
                    Contract Settings
                  </Typography>

                  <FormControl fullWidth>
                    <InputLabel>Contract Type</InputLabel>
                    <Select
                      value={formData.contract_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, contract_type: e.target.value }))}
                      label="Contract Type"
                    >
                      <MenuItem value="simple">
                        <Box>
                          <Typography variant="body2" fontWeight={500}>Simple Agreement</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Single click to accept terms
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="complex">
                        <Box>
                          <Typography variant="body2" fontWeight={500}>Full Signature Contract</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Requires formal digital signatures
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {formData.contract_type === 'complex' && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                        Signature Spots
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<PersonAddIcon />}
                        onClick={addSignatureSpot}
                        fullWidth
                        sx={{ textTransform: 'none', mb: 2 }}
                      >
                        Add Signature Spot
                      </Button>
                      {signatureSpots.length > 0 && (
                        <Stack spacing={1}>
                          {signatureSpots.map((spot) => (
                            <Paper 
                              key={spot.id}
                              variant="outlined"
                              sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                            >
                              <Typography variant="body2">{spot.label}</Typography>
                              <IconButton 
                                size="small"
                                onClick={() => {
                                  const spotText = `{{SIGNATURE_SPOT:${spot.label}:${spot.id}}}`;
                                  const newContent = htmlContent.replace(spotText, '');
                                  updateContentWithHistory(newContent);
                                  setSignatureSpots(prev => prev.filter(s => s.id !== spot.id));
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  )}

                  <Divider />

                  <TextField
                    label="Expiration Period"
                    type="number"
                    value={formData.expires_after_days}
                    onChange={(e) => setFormData(prev => ({ ...prev, expires_after_days: e.target.value }))}
                    fullWidth
                    placeholder="Days"
                    helperText="How many days until this contract expires (optional)"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">days</InputAdornment>
                    }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.requires_resignation_on_update}
                        onChange={(e) => setFormData(prev => ({ ...prev, requires_resignation_on_update: e.target.checked }))}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Require re-signing when updated
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Users must sign again if template changes
                        </Typography>
                      </Box>
                    }
                  />

                  {!userMode && (
                    <>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.is_mandatory}
                            onChange={(e) => setFormData(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                            color="error"
                          />
                        }
                        label={
                          <Box>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <SecurityIcon fontSize="small" color={formData.is_mandatory ? "error" : "disabled"} />
                              <Typography variant="body2" fontWeight={500}>
                                Mandatory Contract
                              </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              Block user actions until signed
                            </Typography>
                          </Box>
                        }
                      />

                      {formData.is_mandatory && (
                        <Alert severity="warning" icon={<SecurityIcon />}>
                          Users will not be able to proceed until they sign this contract
                        </Alert>
                      )}
                    </>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                    <Button
                      onClick={() => setStep(1)}
                      startIcon={<ArrowBackIcon />}
                      sx={{ textTransform: 'none' }}
                      fullWidth
                    >
                      Back to Editor
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={18} /> : <CheckCircleIcon />}
                      sx={{ 
                        textTransform: 'none',
                        bgcolor: '#4F46E5',
                        '&:hover': { bgcolor: '#4338CA' }
                      }}
                      fullWidth
                    >
                      {loading ? 'Saving...' : (template ? 'Update Template' : 'Create Template')}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default EnhancedContractTemplateDialog;